import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { WhatsAppEmailGate } from "./WhatsAppEmailGate";
import { WhatsAppPhoneGate } from "./WhatsAppPhoneGate";

// Routes that must remain accessible during the verification journey
const EXEMPT_ROUTES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/auth/callback",
  "/verify-email",
];

// Routes that get "soft" email gate (user sees page + modal overlay).
// Includes dashboards and event pages so a pending booking can resume after
// WhatsApp OTP login even if the synthetic email isn't verified yet.
const SOFT_GATE_ROUTES = [
  "/dashboard",
  "/dashboard/fan",
  "/dashboard/artist",
  "/dashboard/sponsor",
  "/dashboard/admin",
  "/events",
];

function isExemptRoute(pathname: string): boolean {
  return EXEMPT_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + "/"),
  );
}

function isSoftGateRoute(pathname: string): boolean {
  return SOFT_GATE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + "/"),
  );
}

interface GlobalWhatsAppPolicyGateProps {
  children: React.ReactNode;
}

/**
 * Global gate that enforces WhatsApp email + phone verification
 * for all authenticated users across every route and PWA state.
 *
 * - Guests pass through freely (gates internally check for user).
 * - Auth-related routes are exempt so users can complete login/register flows.
 * - Dashboard routes use "soft" mode: user sees their dashboard with a modal overlay.
 * - All other routes use "hard" mode: full-screen block until verified.
 */
export function GlobalWhatsAppPolicyGate({
  children,
}: GlobalWhatsAppPolicyGateProps) {
  const { user } = useAuth();
  const location = useLocation();

  // No user or exempt route — pass through
  if (!user || isExemptRoute(location.pathname)) {
    return <>{children}</>;
  }

  const emailGateMode = isSoftGateRoute(location.pathname) ? "soft" : "hard";

  // Apply gates: email first (for WhatsApp users), then phone (for fans)
  return (
    <WhatsAppEmailGate mode={emailGateMode}>
      <WhatsAppPhoneGate>{children}</WhatsAppPhoneGate>
    </WhatsAppEmailGate>
  );
}
